import type { CheerioAPI } from "cheerio";
import type { NovelMetadata, ChapterRef, Chapter } from "../../data-model/novel.js";
import type { Url, ChapterIndex } from "../../data-model/primitives.js";
import { unsafeBrandAdapterId } from "../../data-model/primitives.js";
import { ParseError } from "../../core/errors.js";
import { loadHtml, normalizeText, wordCount } from "../../utils/html.js";
import { METRUYENCHU_SELECTORS as S } from "./selectors.js";
import { refsFromAnchors } from "../shared/anchors.js";
import { parseNovelHeader, buildNovelMetadata } from "../shared/header.js";
import { stripLeadingTitle, assertContentNonEmpty } from "../shared/content.js";

const SOURCE_SITE = unsafeBrandAdapterId("metruyenchu-com-vn");

export function parseNovelPage($: CheerioAPI, sourceUrl: Url): NovelMetadata {
  const header = parseNovelHeader($, S.metadata, sourceUrl);

  let status: NovelMetadata["status"] = "unknown";
  if ($(S.metadata.statusFull).length > 0) status = "completed";
  else if (S.metadata.statusOngoing.some((sel) => $(sel).length > 0)) status = "ongoing";

  return buildNovelMetadata(header, {
    sourceUrl,
    sourceSite: SOURCE_SITE,
    status,
    totalChapters: extractTotalChapters($),
  });
}

// Matches the "Số chương" badge inside the book-info-text list:
//   <li><b>Số chương :</b> 2330</li>
// The site is the source of truth for the novel's TOC size; reading it here
// keeps fetched/total decoupled so quick-mode runs that fetch only N chapters
// still report the source's full chapter count, not N.
const TOTAL_CHAPTERS_LABEL_REGEX = /^\s*Số\s*chương\s*:?\s*$/i;
const TOTAL_CHAPTERS_VALUE_REGEX = /(\d[\d.,]*)/;

function extractTotalChapters($: CheerioAPI): number | undefined {
  let result: number | undefined;
  $(S.metadata.infoListItem).each((_, el) => {
    if (result !== undefined) return;
    const li = $(el);
    const labelEl = li.find("b, strong").first();
    if (labelEl.length === 0) return;
    const labelText = labelEl.text().replace(/\s+/g, " ").trim();
    if (!TOTAL_CHAPTERS_LABEL_REGEX.test(labelText)) return;
    // Read the value from the li text nodes excluding the <b>/<strong> label.
    // Avoids fragile slice math when the site adds whitespace or stray text
    // nodes around the label.
    const clone = li.clone();
    clone.find("b, strong").remove();
    const valueText = clone.text().replace(/\s+/g, " ").trim();
    const m = TOTAL_CHAPTERS_VALUE_REGEX.exec(valueText);
    if (!m || !m[1]) return;
    const digits = m[1].replace(/[.,]/g, "");
    const n = Number.parseInt(digits, 10);
    if (Number.isFinite(n) && n > 0) result = n;
  });
  return result;
}

export function parseTocPage1(
  $: CheerioAPI,
  startIndex: ChapterIndex,
  baseUrl: Url,
): ChapterRef[] {
  return refsFromAnchors($, { selector: S.toc.listItem, startIndex, baseUrl });
}

export function parseTocJsonPage(
  htmlFragment: string,
  startIndex: ChapterIndex,
  baseUrl: Url,
): ChapterRef[] {
  const $ = loadHtml(htmlFragment);
  return refsFromAnchors($, { selector: S.toc.jsonListItem, startIndex, baseUrl });
}

export function extractBookId($: CheerioAPI): string | undefined {
  const v = $(S.metadata.bookIdInput).first().attr("value")?.trim();
  return v && v.length > 0 ? v : undefined;
}

export function extractLastPageNumber($: CheerioAPI): number {
  let max = 0;
  $(S.metadata.pagingPageFn).each((_, el) => {
    const onclick = $(el).attr("onclick") ?? "";
    const m = /page\(\s*\d+\s*,\s*(\d+)\s*\)/.exec(onclick);
    if (m && m[1]) {
      const n = Number.parseInt(m[1], 10);
      if (n > max) max = n;
    }
  });
  return max > 0 ? max : 1;
}

export function parseChapterPage($: CheerioAPI, ref: ChapterRef): Chapter {
  const body = $(S.chapter.body);
  if (body.length === 0) {
    throw new ParseError("Missing #vungdoc on chapter page", { url: ref.url });
  }

  // Capture in-page title before stripping nav containers (which may include the title element).
  const inPageTitle = $(S.chapter.title).first().text().replace(/\s+/g, " ").trim();

  for (const sel of S.chapter.stripNavSelectors) body.find(sel).remove();
  body.find("script, style").remove();
  body.find("a").each((_, el) => {
    const text = $(el).text().replace(/\s+/g, " ").trim();
    if (S.chapter.navTextRegex.test(text)) $(el).remove();
  });

  const inner = body.html() ?? "";
  let content = normalizeText(inner);

  content = stripLeadingTitle(content, inPageTitle);

  assertContentNonEmpty(content, ref.url);

  const title = inPageTitle.length > 0 ? inPageTitle : ref.title;

  return {
    ...ref,
    title,
    content,
    fetchedAt: new Date(),
    wordCount: wordCount(content),
  };
}
